// Request plugin.
import { injectAgentVersionHeader, customAgentVersionHeaderSuffix } from '../interface/selectors'

// Other plugins.
import { getPlatform, getRequestInfo } from '../../auth/interface/selectors'
import { logManager } from '../../logs'

// Utils.
import { getVersion } from '../../common/version'
import { toQueryString } from '../../common/utils'
import { platforms } from '../../constants'

// Libraries.
import { select } from 'redux-saga/effects'
import { cloneDeep } from 'lodash/fp'

const log = logManager.getLogger('REQUEST')

/**
 * Helper function to compile the common headers/options for a REST request.
 * @method getCommonOptions
 * @param  {string} url The URL to be used for the request.
 * @return {Object}
 */
export function * getCommonOptions (url) {
  const platform = yield select(getPlatform)
  const requestInfo = yield select(getRequestInfo, platform)
  const useCustomHeader = yield select(injectAgentVersionHeader)
  const customSuffix = yield select(customAgentVersionHeaderSuffix)

  // Start off with whatever options were set by the Auth plugin.
  //    The authentication headers / token will be there if they are set.
  let options
  if (platform === 'cpaas') {
    // For some reason, CPaaS has this property as `options` where the other
    //    SDKs have it as `requestOptions`. Workaround that here.
    options = requestInfo.options || {}
  } else {
    options = requestInfo.requestOptions || {}
  }

  // Ensure there is a headers object.
  options.headers = options.headers || {}

  // Add the common headers.
  options.headers['Content-Type'] = 'application/json'
  options.headers.Accept = 'application/json'

  // If enabled, add the 'Agent Version' header to the options.
  if (useCustomHeader) {
    let headerValue = getCpaasAgentHeaderValue(platform, url)
    if (customSuffix) {
      // If a custom suffix value was provided as well, then use it.
      headerValue += ' ' + customSuffix
    }
    options.headers['X-Cpaas-Agent'] = headerValue
  }

  return options
}

/**
 * Determine and return the current SDK platform and version string to be used
 * with the x-Cpaas-Agent network request header field.
 * @method getCpaasAgentHeaderValue
 * @param  {string}  platform Which platform is currently being used.
 * @param  {string}  url The url for the request being made.
 * @return {string}  A string representation of the platform and version the SDK is using.
 */
export function getCpaasAgentHeaderValue (platform, url) {
  // Assume request is for CPaaS platform, by default.
  let headerValue = `cpaas-js-sdk/${getVersion()}`

  // Check if request is for callMe service, otherwise determine the appropriate platform.
  // (callMe service uses Link platform for call requests)
  if (url && url.includes('/anonymous/')) {
    headerValue = `callme-js-sdk/${getVersion()}`
  } else {
    // Check the actual platform used
    if (platform === platforms.UC) {
      headerValue = `uc-js-sdk/${getVersion()}`
    } else if (platform === platforms.LINK) {
      headerValue = `link-js-sdk/${getVersion()}`
    }
  }
  return headerValue
}

/**
 * Prepares a REST request options object for logging by removing passwords
 * and parses the body string into an object that can be interacted with in console.
 * @method sanitizeRequest
 * @param {Object} options An object containing any custom settings that you want to
 *                         apply to the request. See [fetch API](https://developer.mozilla.org/en-US/docs/Web/API/fetch)
 *                         or a full description and defaults.
 * @return {Object} A sanitized REST request options object that can be safely logged.
 */
export function sanitizeRequest (options) {
  const logOptions = cloneDeep(options)
  // When logging the Auth header, cut it off so that we can see the type of
  //    token but not the token itself. Depending on the type, it can contain
  //    a password.
  const authHeader = logOptions.headers && logOptions.headers.Authorization
  if (authHeader) {
    logOptions.headers.Authorization = authHeader.substring(0, 6) + '...'
  }

  // If the rest request body is a string, try to parse it as JSON and store
  // it as an object so it can be interacted with in the console
  const body = logOptions.body
  if (body) {
    try {
      logOptions.body = JSON.parse(body)
    } catch (e) {
      // Noop, leave the body in whatever format it was in.
    }
  }

  return logOptions
}

/**
 * Send a REST request to Link with the appropriate configuration.
 * @method fetchResource
 * @param {string} resource The path of the resource to fetch from the underlying service. This path will be appended
 *                          to the base URL according to SDK configuration.
 * @param {Object} requestInfo An object containing any custom settings that you want to apply to the request. See [fetch API](https://developer.mozilla.org/en-US/docs/Web/API/fetch)
 *                             for a full description and defaults.
 * @return {Promise<Response>} A promise for a [Response](https://developer.mozilla.org/en-US/docs/Web/API/Response) object.
 */
export async function fetchResource (resource, requestInfo, module) {
  const { requestOptions } = requestInfo

  // If the app doesn't prepend the '/' we should add it.
  if (resource.charAt() !== '/') {
    resource = `/${resource}`
  }

  const url = `${requestInfo.baseURL}${resource}`
  const logOptions = sanitizeRequest(requestOptions)
  log.debug('Making REST request', logOptions)

  const queryParams = requestInfo.requestOptions.queryParams || {}
  let queryString = toQueryString(queryParams)
  // Check resource passed in for any existing query params
  if (resource.includes('?')) {
    queryString = queryString.replace('?', '&')
  }
  const response = await fetch(url + queryString, requestOptions)

  log.debug('Received REST response', response)

  return response
}
